import type { SceneEntity } from '@seddi/scene';

import type { SceneRenderable } from '../renderables/sceneRenderable';
import type { GarmentGeometryGfx } from '../resources/garmentGeometryTypes';
import type { ResourcesContext } from '../resources/resourcesContext';
import type { StitchesTextures } from '../resources/stitchesTexturesTypes';
import type { SceneDesc } from '../scene/sceneTypes';
import type { Theme } from '../theme/themeTypes';
import type { SceneReconcilerConfig } from './sceneReconcilerConfig';

export type SceneReconcilerFrameEntity = {
  childrenDiff: { prev?: SceneEntity; next: SceneEntity }[];
  data: SceneEntity;
  isGeometryDirty?: boolean;
  isPiece?: boolean;
};

export type SceneReconcilerFrame = {
  readonly config: SceneReconcilerConfig;
  readonly entities: Map<string, SceneReconcilerFrameEntity>;
  nextGarmentGeometry?: GarmentGeometryGfx;
  nextGarmentGeometryId?: string;
  nextParent?: SceneEntity;
  nextScene?: SceneDesc;
  nextUseGarmentGeometry: boolean;
  prevGarmentGeometry?: GarmentGeometryGfx;
  prevGarmentGeometryId?: string;
  prevUseGarmentGeometry: boolean;
  prevParent?: SceneEntity;
  prevScene?: SceneDesc;
  readonly resources: ResourcesContext;
  readonly scene: SceneRenderable;
  stitchesTextures?: StitchesTextures;
  readonly theme: Theme;
};

/**
 * Create.
 * @param config - Config.
 * @param resources - Resources context.
 * @param scene - Scene renderable.
 * @param theme - Theme.
 */
export const create = (
  config: SceneReconcilerConfig,
  resources: ResourcesContext,
  scene: SceneRenderable,
  theme: Theme,
): SceneReconcilerFrame => {
  return {
    config,
    entities: new Map(),
    nextUseGarmentGeometry: false,
    prevUseGarmentGeometry: false,
    resources,
    scene,
    theme,
  };
};

/**
 * Reset at the beginning of each frame.
 * @param self - Scene reconciler frame.
 */
export const reset = (self: SceneReconcilerFrame) => {
  self.prevGarmentGeometry = self.nextGarmentGeometry;
  self.prevScene = self.nextScene;
  self.prevUseGarmentGeometry = self.nextUseGarmentGeometry;
  // Clear fields.
  self.entities.clear();
  // Do not reset self.nextGarmentGeometry, reconciliation only writes when a state change occurs.
  self.nextScene = undefined;
  self.nextUseGarmentGeometry = false;
  self.stitchesTextures = undefined;
};
